import React, { useEffect, useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  listCurated, createCurated, updateCurated, deleteCurated, testCurated, formatCurated,
} from '../../api/client';

const PRODUCT_CHOICES = [
  'Perform', 'E-bridge',
  'eScription One',
  'InQuiry', 'InCommand', 'InScribe', 'InSync', 'InTouch',
  'InstaNote', 'InVision',
  'ESOne Mobile',
  'ShadowLink', 'ShadowPrint',
];

const empty = () => ({
  question: '',
  answer_md: '',
  products: [],
  citation_url: '',
  active: true,
});

export default function CuratedAnswersTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);     // null | empty() | existing record
  const [testQ, setTestQ] = useState('');
  const [testResult, setTestResult] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listCurated();
      setItems(res.data.items || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load curated answers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const onSave = async () => {
    try {
      const payload = {
        question: editing.question.trim(),
        answer_md: editing.answer_md,
        products: editing.products || [],
        citation_url: editing.citation_url || null,
        active: editing.active !== false,
      };
      if (editing.id) await updateCurated(editing.id, payload);
      else await createCurated(payload);
      setEditing(null);
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Save failed');
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm('Delete this curated answer? This cannot be undone.')) return;
    try {
      await deleteCurated(id);
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Delete failed');
    }
  };

  const onToggleActive = async (rec) => {
    try {
      await updateCurated(rec.id, { active: !rec.active });
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Toggle failed');
    }
  };

  const onTest = async () => {
    if (!testQ.trim()) return;
    try {
      const res = await testCurated(testQ.trim());
      setTestResult(res.data);
    } catch (err) {
      setTestResult({ error: err.response?.data?.detail || 'Test failed' });
    }
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>Curated Answers</h2>
          <p style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
            SME-vetted Q/A pairs. When a user's question matches one closely, DASH returns this answer
            <strong> verbatim</strong>, bypassing the KB.
          </p>
        </div>
        <button
          onClick={() => setEditing(empty())}
          style={{
            padding: '10px 18px',
            background: 'linear-gradient(135deg, #0d4f52, #1d9c9c)',
            color: '#fff', border: 'none',
            borderRadius: 'var(--radius-btn, 12px)',
            fontWeight: 700, fontSize: 14, cursor: 'pointer',
            boxShadow: '0 6px 16px rgba(13,79,82,0.25)',
          }}
        >
          + Add Manual Question
        </button>
      </div>

      {error && (
        <div style={{
          padding: '10px 14px', marginBottom: 14,
          background: '#fff5f5', border: '1px solid #feb2b2',
          borderRadius: 8, color: '#c53030', fontSize: 13,
        }}>{error}</div>
      )}

      {/* Try-it panel */}
      <div style={{
        border: '1px solid rgba(29,156,156,0.25)',
        background: 'rgba(29,156,156,0.05)',
        borderRadius: 12, padding: 14, marginBottom: 18,
      }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#0d7377', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
          Try it — see which curated answer (if any) would fire
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={testQ}
            onChange={(e) => setTestQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') onTest(); }}
            placeholder='e.g., "How do I reset my password?"'
            style={{
              flex: 1, padding: '9px 12px',
              border: '1px solid #cbd5e1', borderRadius: 8,
              fontSize: 13.5, outline: 'none',
            }}
          />
          <button
            onClick={onTest}
            style={{
              padding: '9px 18px',
              background: '#fff', color: '#0d7377',
              border: '1px solid #1d9c9c', borderRadius: 8,
              fontSize: 13, fontWeight: 700, cursor: 'pointer',
            }}
          >Test</button>
        </div>
        {testResult && (
          <div style={{ marginTop: 10, fontSize: 13 }}>
            {testResult.error ? (
              <span style={{ color: '#c53030' }}>{testResult.error}</span>
            ) : testResult.matched ? (
              <span style={{ color: '#0d7377' }}>
                ✓ Match — <strong>#{testResult.result.id}</strong>:{' '}
                "{testResult.result.question}" (similarity {testResult.result.match_similarity})
              </span>
            ) : testResult.best_candidate ? (
              <div style={{ color: '#64748b' }}>
                ✗ No match — closest: <strong>#{testResult.best_candidate.id}</strong>{' '}
                "{testResult.best_candidate.question}" at similarity{' '}
                <strong>{testResult.similarity}</strong> (needs ≥ {testResult.threshold})
                {!testResult.best_candidate.active && (
                  <span style={{ color: '#c53030' }}> · inactive</span>
                )}
              </div>
            ) : (
              <span style={{ color: '#64748b' }}>
                ✗ No curated answers configured yet.
              </span>
            )}
          </div>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>Loading…</div>
      ) : items.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: 50,
          border: '1px dashed #cbd5e1', borderRadius: 12,
          color: '#64748b', background: '#f8fafc',
        }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>📝</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>No curated answers yet</div>
          <div style={{ fontSize: 12.5, marginTop: 4 }}>
            Click <strong>+ Add Manual Question</strong> to write your first vetted answer.
          </div>
        </div>
      ) : (
        <div style={{
          border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden',
          background: '#fff', boxShadow: '0 4px 14px rgba(13,79,82,0.06)',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
            <thead>
              <tr style={{ background: '#f1f5f9', fontSize: 11.5, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                <th style={th}>Question</th>
                <th style={th}>Products</th>
                <th style={{ ...th, textAlign: 'center' }}>Hits</th>
                <th style={{ ...th, textAlign: 'center' }}>Status</th>
                <th style={{ ...th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((rec) => (
                <tr key={rec.id} style={{ borderTop: '1px solid #e2e8f0' }}>
                  <td style={td}>
                    <div style={{ fontWeight: 600, color: '#0f172a' }}>{rec.question}</div>
                    <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 2 }}>
                      {rec.added_by ? `by ${rec.added_by} · ` : ''}
                      {new Date(rec.updated_at + 'Z').toLocaleString()}
                    </div>
                  </td>
                  <td style={td}>
                    {rec.products?.length ? rec.products.map((p) => (
                      <span key={p} style={pill}>{p}</span>
                    )) : <span style={{ color: '#94a3b8' }}>Any</span>}
                  </td>
                  <td style={{ ...td, textAlign: 'center', fontWeight: 700, color: '#0d7377' }}>{rec.hit_count}</td>
                  <td style={{ ...td, textAlign: 'center' }}>
                    <button
                      onClick={() => onToggleActive(rec)}
                      title={rec.active ? 'Click to deactivate' : 'Click to activate'}
                      style={{
                        padding: '3px 10px',
                        borderRadius: 999,
                        border: 'none',
                        fontSize: 11, fontWeight: 700,
                        cursor: 'pointer',
                        background: rec.active ? 'rgba(29,156,156,0.15)' : '#f1f5f9',
                        color: rec.active ? '#0d7377' : '#94a3b8',
                      }}
                    >
                      {rec.active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <button onClick={() => setEditing(rec)} style={iconBtn}>Edit</button>
                    <button onClick={() => onDelete(rec.id)} style={{ ...iconBtn, color: '#c53030' }}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <EditModal
          record={editing}
          onChange={setEditing}
          onClose={() => setEditing(null)}
          onSave={onSave}
        />
      )}
    </div>
  );
}

const th = { padding: '11px 14px', textAlign: 'left', fontWeight: 700 };
const td = { padding: '12px 14px', verticalAlign: 'top' };
const pill = {
  display: 'inline-block', padding: '2px 9px',
  background: 'rgba(29,156,156,0.10)', color: '#0d7377',
  borderRadius: 999, fontSize: 11, fontWeight: 600,
  marginRight: 4, marginBottom: 2,
};
const iconBtn = {
  background: 'transparent', border: 'none',
  color: '#475569', cursor: 'pointer',
  fontSize: 13, fontWeight: 600,
  padding: '4px 10px',
};

// ── Edit / Add modal ────────────────────────────────────────────────────────
function EditModal({ record, onChange, onClose, onSave }) {
  const isNew = !record.id;
  const [formatting, setFormatting] = useState(false);
  const [formatErr, setFormatErr] = useState('');

  const onAutoFormat = async () => {
    if (!record.answer_md?.trim()) return;
    setFormatErr('');
    setFormatting(true);
    try {
      const res = await formatCurated(record.answer_md, record.question || '');
      const next = res.data?.formatted?.trim();
      if (next) onChange({ ...record, answer_md: next });
    } catch (err) {
      setFormatErr(err.response?.data?.detail || 'Auto-format failed');
    } finally {
      setFormatting(false);
    }
  };

  const toggleProduct = (p) => {
    const has = (record.products || []).includes(p);
    onChange({
      ...record,
      products: has
        ? record.products.filter((x) => x !== p)
        : [...(record.products || []), p],
    });
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 2500,
        background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(3px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(880px, 100%)', maxHeight: '90vh',
          background: '#fff', borderRadius: 16,
          boxShadow: '0 30px 60px rgba(13,79,82,0.30)',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '18px 22px',
          background: 'linear-gradient(135deg, #0d4f52, #1d9c9c)',
          color: '#fff',
          borderRadius: '16px 16px 0 0',
        }}>
          <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: '-0.01em' }}>
            {isNew ? 'Add Manual Question' : `Edit Curated Answer #${record.id}`}
          </div>
          <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>
            DASH will return this answer verbatim when a user's question matches semantically.
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 22px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
          {/* Left column — inputs */}
          <div>
            <Label>Question</Label>
            <textarea
              value={record.question || ''}
              onChange={(e) => onChange({ ...record, question: e.target.value })}
              placeholder='How do I reset my InCommand password?'
              rows={3}
              style={input}
            />

            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginTop: 14, marginBottom: 6 }}>
              <div style={{
                fontSize: 11, fontWeight: 700, color: '#475569',
                textTransform: 'uppercase', letterSpacing: '0.07em',
              }}>Answer (Markdown)</div>
              <button
                type="button"
                onClick={onAutoFormat}
                disabled={formatting || !record.answer_md?.trim()}
                title="Reformat your raw notes into the same Markdown style DASH uses in chat"
                style={{
                  fontSize: 11.5, fontWeight: 700,
                  padding: '4px 10px',
                  border: '1px solid #1d9c9c',
                  background: formatting ? '#e0f2f1' : '#fff',
                  color: '#0d7377',
                  borderRadius: 999,
                  cursor: (formatting || !record.answer_md?.trim()) ? 'not-allowed' : 'pointer',
                  opacity: !record.answer_md?.trim() ? 0.5 : 1,
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                }}
              >
                {formatting ? '⏳ Formatting…' : '✨ Auto-format with AI'}
              </button>
            </div>
            <textarea
              value={record.answer_md || ''}
              onChange={(e) => onChange({ ...record, answer_md: e.target.value })}
              placeholder={'Paste raw notes/steps here, then click "Auto-format with AI" — or write Markdown directly:\n\n1. Open the login page\n2. Click Forgot Password\n3. ...'}
              rows={12}
              style={{ ...input, fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: 13 }}
            />
            {formatErr && (
              <div style={{ fontSize: 12, color: '#c53030', marginTop: 4 }}>{formatErr}</div>
            )}

            <Label>Citation URL (optional)</Label>
            <input
              type="url"
              value={record.citation_url || ''}
              onChange={(e) => onChange({ ...record, citation_url: e.target.value })}
              placeholder="https://docs.escription-one.com/..."
              style={input}
            />

            <Label>Applies to products (empty = any)</Label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {PRODUCT_CHOICES.map((p) => {
                const on = (record.products || []).includes(p);
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => toggleProduct(p)}
                    style={{
                      padding: '5px 11px',
                      borderRadius: 999, border: 'none',
                      fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      background: on ? '#1d9c9c' : '#f1f5f9',
                      color: on ? '#fff' : '#475569',
                    }}
                  >{p}</button>
                );
              })}
            </div>

            <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                id="curated-active"
                type="checkbox"
                checked={record.active !== false}
                onChange={(e) => onChange({ ...record, active: e.target.checked })}
              />
              <label htmlFor="curated-active" style={{ fontSize: 13, color: '#475569' }}>
                <strong>Active</strong> — DASH will use this answer when matched
              </label>
            </div>
          </div>

          {/* Right column — preview */}
          <div>
            <Label>Live preview</Label>
            <div style={{
              border: '1px solid #e2e8f0', borderRadius: 10,
              padding: 14, minHeight: 360,
              background: '#f8fafc', fontSize: 13.5, color: '#0f172a',
              lineHeight: 1.6, overflowY: 'auto', maxHeight: 460,
            }}>
              <ReactMarkdown>{record.answer_md || '*Start typing the answer to see the preview.*'}</ReactMarkdown>
              {record.citation_url && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed #cbd5e1', fontSize: 12.5 }}>
                  <strong>Reference:</strong>{' '}
                  <a href={record.citation_url} target="_blank" rel="noreferrer">{record.citation_url}</a>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '14px 22px',
          borderTop: '1px solid #e2e8f0',
          display: 'flex', justifyContent: 'flex-end', gap: 10,
        }}>
          <button onClick={onClose} style={{
            padding: '9px 18px', border: '1px solid #cbd5e1',
            background: '#fff', borderRadius: 'var(--radius-btn, 12px)',
            color: '#475569', fontWeight: 600, fontSize: 13.5, cursor: 'pointer',
          }}>Cancel</button>
          <button
            onClick={onSave}
            disabled={!record.question?.trim() || !record.answer_md?.trim()}
            style={{
              padding: '9px 22px',
              background: 'linear-gradient(135deg, #0d4f52, #1d9c9c)',
              color: '#fff', border: 'none',
              borderRadius: 'var(--radius-btn, 12px)',
              fontWeight: 700, fontSize: 13.5, cursor: 'pointer',
              opacity: (!record.question?.trim() || !record.answer_md?.trim()) ? 0.5 : 1,
            }}
          >
            {isNew ? 'Save Question' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

const Label = ({ children }) => (
  <div style={{
    fontSize: 11, fontWeight: 700, color: '#475569',
    textTransform: 'uppercase', letterSpacing: '0.07em',
    marginBottom: 6, marginTop: 14,
  }}>{children}</div>
);

const input = {
  width: '100%', padding: '9px 12px',
  border: '1px solid #cbd5e1', borderRadius: 8,
  fontSize: 13.5, outline: 'none', resize: 'vertical',
  boxSizing: 'border-box',
};
